import React from "react";
import glamorous from "glamorous";
import { InputGroup, FormControl } from "react-bootstrap";
import _ from "lodash";

import MapView from "../map/MapView";
import colors from "../../styles/colors";
import { SelectElem } from "../../vendor/signal-widgets/forms";
import { LadChicletCSS as Chiclet } from "../../components/chiclets";
import { getLadName, LOBS } from "../../components/lads";
import { FlexDiv, FlexColDiv } from "../../styles/container-elements";

import DoubleArrowR from "react-icons/lib/fa/angle-double-right";
import DoubleArrowL from "react-icons/lib/fa/angle-double-left";
import Search from "react-icons/lib/fa/search";
import CloseCircle from "react-icons/lib/fa/times-circle-o";

const Section = glamorous.section({
  display: "flex",
  height: "calc(100vh - 3em)",
  flexDirection: "column",
  justifyContent: "space-between",
  backgroundColor: "white"
});

const formattedStartCase = str => (str ? _.startCase(_.toLower(str)) : "");

const BackBtn = ({
  style,
  showLadsPanel,
  height = 60,
  width = 60,
  selectedLad,
  ...props
}) => {
  let iconElem = <DoubleArrowL />;

  if (showLadsPanel && !selectedLad) iconElem = <DoubleArrowR />;
  if (!showLadsPanel && selectedLad) iconElem = <CloseCircle />;

  let closeElem =
    (showLadsPanel && !selectedLad) || (!showLadsPanel && selectedLad) ? (
      <glamorous.Div
        style={{ fontSize: 10, fontStyle: "italic", marginTop: "1em" }}
      >
        Close
      </glamorous.Div>
    ) : null;

  return (
    <glamorous.Figure
      css={{
        alignItems: "center",
        backgroundColor: colors.bgColorDarkStripes(0),
        borderRadius: "3px",
        display: "flex",
        height: height,
        flex: "0 0 100%",
        flexDirection: "column",
        justifyContent: "space-around",
        width: width,
        ...style
      }}
      {...props}
    >
      <glamorous.Figcaption
        css={{
          color: "white",
          fontSize: 24,
          fontWeight: 400,
          textTransform: "uppercase",
          textAlign: "center"
        }}
      >
        {iconElem}
        {closeElem}
      </glamorous.Figcaption>
    </glamorous.Figure>
  );
};

const MapNavItem = ({ lad, ind, showLadsPanel, eventHandler, active }) => {
  return (
    <FlexDiv
      css={{
        backgroundColor: colors.bgColorDarkStripes(ind),
        flex: "0 0 100%",
        ":hover": {
          backgroundColor: colors.highlight.LIGHT_BLUE,
          cursor: "pointer"
        }
      }}
      onClick={() => eventHandler(lad, "SELECT_LAD")}
    >
      <Chiclet lad={lad} active={active} showLadLabel showLobLabel square />
      {showLadsPanel ? (
        <FlexDiv
          css={{
            alignItems: "center",
            color: "white",
            fontWeight: 400,
            marginLeft: ".5em"
          }}
        >
          {getLadName(lad)}
        </FlexDiv>
      ) : null}
    </FlexDiv>
  );
};

const LadsList = ({
  eventHandler,
  ladsGrouped,
  showLadsPanel,
  selectedLad,
  selectedLob
}) => {
  let lob = _.get(LOBS, selectedLob);
  let mapNavElem;

  if (!_.isEmpty(ladsGrouped) && lob) {
    mapNavElem = ladsGrouped[lob].map((lad, i) => (
      <MapNavItem
        key={i}
        lad={lad}
        ind={i}
        showLadsPanel={showLadsPanel}
        eventHandler={eventHandler}
        active={selectedLad === lad}
      />
    ));
  }

  let businessElem = showLadsPanel ? (
    <FlexColDiv
      css={{
        flex: 1,
        marginBottom: ".25em",
        paddingLeft: "1em",
        paddingRight: "1em"
      }}
    >
      <FlexDiv
        css={{
          paddingTop: "1em",
          marginBottom: ".5em",
          color: "white"
        }}
      >
        Line of Business
      </FlexDiv>
      <SelectElem
        style={{ flex: "0 0 100%", marginBottom: ".5em" }}
        list={LOBS}
        lbl=""
        evt="SELECT_LOB"
        handler={eventHandler}
        val={selectedLob}
      />
    </FlexColDiv>
  ) : null;

  return (
    <FlexColDiv>
      <FlexDiv
        css={{
          display: "inline-flex",
          minHeight: "6.5em"
        }}
      >
        <FlexColDiv>
          <BackBtn
            style={{
              cursor: "pointer"
            }}
            onClick={() => eventHandler(!showLadsPanel, "TOGGLE_PANEL")}
            showLadsPanel={showLadsPanel}
            selectedLad={selectedLad}
          />
        </FlexColDiv>
        {businessElem}
      </FlexDiv>
      <FlexColDiv
        css={{
          flex: 1,
          overflowY: "auto"
        }}
      >
        {mapNavElem}
      </FlexColDiv>
    </FlexColDiv>
  );
};

const LocItem = ({ loc, ind, eventHandler }) => {
  return (
    <FlexDiv
      css={{
        alignItems: "center",
        backgroundColor: colors.bgColorDarkStripes(ind),
        color: "white",
        flex: "0 0 100%",
        fontSize: "small",
        minHeight: "2.5em",
        padding: ".25em",
        ":hover": {
          backgroundColor: colors.highlight.LIGHT_BLUE,
          cursor: "pointer"
        }
      }}
      onClick={() => eventHandler(loc, "LOCATION_LINK")}
    >
      <FlexDiv
        css={{
          flex: 0.6,
          fontWeight: 600,
          marginLeft: ".5em",
          textDecoration: "underline"
        }}
      >
        {formattedStartCase(loc.name)}
      </FlexDiv>
      <FlexDiv
        css={{
          color: colors.text.LIGHT_GRAY,
          flex: 0.4,
          marginLeft: ".75em",
          marginRight: ".5em"
        }}
      >
        {`${formattedStartCase(loc.city)}, ${loc.state}`}
      </FlexDiv>
    </FlexDiv>
  );
};

const LocationSearchBar = ({
  placeholderStr,
  searchStr,
  setSearchStr,
  style
}) => {
  return (
    <glamorous.Div
      css={{
        display: "flex",
        flexDirection: "row",
        marginBottom: "1em",
        ...style
      }}
    >
      <InputGroup
        style={{
          border: "1px solid rgba(0, 0, 0, 0.1)",
          boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.1)",
          borderRadius: "3px",
          flex: 1
        }}
      >
        <Search
          style={{
            color: colors.background.DARK_BLUE,
            position: "absolute",
            left: "0.8em",
            top: "0.85em",
            zIndex: 1
          }}
        />
        <FormControl
          type="text"
          onChange={e => setSearchStr(e.target.value)}
          placeholder={placeholderStr}
          value={searchStr}
          style={{
            backgroundColor: "white",
            border: "none",
            boxShadow: "none",
            flex: 1,
            height: "2.8em",
            paddingLeft: "2.4em",
            borderRadius: "3px",
            zIndex: 0
          }}
        />
      </InputGroup>
    </glamorous.Div>
  );
};

const LocationsList = ({
  locations,
  eventHandler,
  selectedLad,
  searchStr,
  setSearchStr
}) => {
  let locItemElem = locations
    ? locations.map((loc, i) => (
        <LocItem key={i} loc={loc} ind={i} eventHandler={eventHandler} />
      ))
    : null;

  let ladLbl = selectedLad ? getLadName(selectedLad) : "";

  return (
    <FlexColDiv>
      <FlexColDiv
        css={{
          alignItem: "center",
          display: "inline-flex",
          fontSize: "small",
          justifyContent: "center",
          minHeight: "6.75em",
          padding: ".5em"
        }}
      >
        <FlexDiv css={{ flex: 1 }}>
          <LocationSearchBar
            style={{ flex: 1 }}
            placeholderStr={`Search ${ladLbl}s`}
            searchStr={searchStr}
            setSearchStr={setSearchStr}
          />
        </FlexDiv>
        <FlexDiv
          css={{
            alignItems: "center",
            flex: 1,
            justifyContent: "center",
            marginBottom: ".25em"
          }}
        >
          {`${locations ? _.size(locations) : 0} ${ladLbl}s`}
        </FlexDiv>
      </FlexColDiv>
      <FlexColDiv
        css={{
          flex: 1,
          overflowY: "auto"
        }}
      >
        {locItemElem}
      </FlexColDiv>
    </FlexColDiv>
  );
};

function getMapFlex(showLadsPanel, selectedLad) {
  if (!showLadsPanel) {
    if (selectedLad == null) return 0.95;
    else return 0.6;
  }

  return 0.7;
}

class EndToEndView extends React.Component {
  constructor(props) {
    super(props);

    this.eventHandler = this.eventHandler.bind(this);
  }

  eventHandler(val, type) {
    const {
      searchLocation,
      selectLob,
      selectLocation,
      selectLad,
      selectedLad,
      toggleLadsPanel
    } = this.props;

    switch (type) {
      case "SELECT_LOB":
        selectLob(parseInt(val, 10));
        break;
      case "SELECT_LOCATION":
        selectLocation(val);
        break;
      case "SELECT_LAD":
        selectLad(val);
        break;
      case "TOGGLE_PANEL":
        if (val === true && selectedLad !== null) selectLad(null);
        toggleLadsPanel(val);
        break;
      case "LOCATION_LINK":
        searchLocation(val.name, val.id);
        break;
      default:
        break;
    }
  }

  render() {
    const {
      H,
      platform,
      ladsGrouped,
      locations,
      searchStr,
      selectedLad,
      selectedLob,
      selectedLocation,
      setSearchStr,
      showLadsPanel,
      ladsList
    } = this.props;

    // The first pass of filtering is done based upon the LOB
    // Create a list of accepted LAD ID's based upon the
    // selected Lob
    let selectedLobName = _.get(LOBS, selectedLob);

    const allowedLads = ladsList.filter(l => {
      return l.lob_name === selectedLobName;
    });

    // Now that we have the LADS which are allowed
    // filter our location list
    const allowedLocations = locations.filter(loc => {
      // Is the lad of this location in our lis
      // of allowed lads
      if (loc.lad) {
        //    console.log(loc.lad.id);
        let found = allowedLads.find(
          lad => Number(lad.id) === Number(loc.lad.id)
        );
        return found !== undefined;
      }
      return false;
    });

    // Now we can filter the remaining locations
    // based upon selected LAD and search string
    let filteredLocations = [];
    if (selectedLad != null) {
      if (searchStr != null) {
        filteredLocations = allowedLocations.filter(l => {
          return (
            Number(l.lad.id) === Number(selectedLad.id) &&
            ((l.name &&
              l.name.toLowerCase().includes(searchStr.toLowerCase())) ||
              (l.city &&
                l.city.toLowerCase().includes(searchStr.toLowerCase())) ||
              (l.state &&
                l.state.toLowerCase().includes(searchStr.toLowerCase())))
          );
        });
      } else {
        filteredLocations = allowedLocations.filter(l => {
          return Number(l.lad.code) === Number(selectedLad.code);
        });
      }
    }

    return (
      <FlexDiv>
        <Section
          css={{
            flex: getMapFlex(showLadsPanel, selectedLad)
          }}
        >
          <MapView
            mapLocations={filteredLocations}
            selectedLad={selectedLad}
            selectedLocation={selectedLocation}
            H={H}
            platform={platform}
            markerIsClickable={true}
            eventHandler={this.eventHandler}
          />
        </Section>
        {selectedLad != null && !showLadsPanel ? (
          <Section
            css={{
              backgroundColor: colors.locationsHeaderBackground,
              color: "white",
              flex: 0.35
            }}
          >
            <LocationsList
              eventHandler={this.eventHandler}
              locations={filteredLocations}
              selectedLad={selectedLad}
              searchStr={searchStr}
              setSearchStr={setSearchStr}
            />
          </Section>
        ) : null}
        <Section
          css={{
            backgroundColor: colors.locationsHeaderBackground,
            flex: showLadsPanel ? 0.3 : 0.05
          }}
        >
          <LadsList
            eventHandler={this.eventHandler}
            ladsGrouped={ladsGrouped}
            selectedLad={selectedLad}
            selectedLob={selectedLob}
            showLadsPanel={showLadsPanel}
          />
        </Section>
      </FlexDiv>
    );
  }
}

export default EndToEndView;



// WEBPACK FOOTER //
// ./src/modules/end-to-end/EndToEndView.js